import { AxiosInstance } from 'axios'
import { BaseHTTP } from './base.http'

type InventoryQueryParams = Record<string, string | number | boolean | undefined>
type InventoryPayload = Record<string, unknown>
type UploadInitialStockOptions = {
  warehouseId?: number | string
  warehouseCode?: string
}

class InventoryAPI {
  private http!: AxiosInstance

  constructor() {
    this.http = BaseHTTP.getInstance().http
  }

  createWarehouse(businessId: number, payload: InventoryPayload) {
    return this.http.post(`v1/business/${businessId}/inventory/warehouses`, payload)
  }

  getWarehouses(businessId: number) {
    return this.http.get(`v1/business/${businessId}/inventory/warehouses`)
  }

  deleteWarehouse(businessId: number, warehouseId: number) {
    return this.http.delete(`v1/business/${businessId}/inventory/warehouses/${warehouseId}`)
  }

  updateWarehouse(businessId: number, warehouseId: number, payload: InventoryPayload) {
    return this.http.patch(`v1/business/${businessId}/inventory/warehouses/${warehouseId}`, payload)
  }

  createItem(businessId: number, payload: InventoryPayload) {
    return this.http.post(`v1/business/${businessId}/inventory/items`, payload)
  }

  updateItem(businessId: number, itemId: number, payload: InventoryPayload) {
    return this.http.patch(`v1/business/${businessId}/inventory/items/${itemId}`, payload)
  }

  deleteItem(businessId: number, itemId: number) {
    return this.http.delete(`v1/business/${businessId}/inventory/items/${itemId}`)
  }

  getItems(businessId: number, params: InventoryQueryParams = {}) {
    return this.http.get(`v1/business/${businessId}/inventory/items`, { params })
  }

  getStock(businessId: number, params: InventoryQueryParams = {}) {
    return this.http.get(`v1/business/${businessId}/inventory/stock`, { params })
  }

  getMovements(businessId: number, params: InventoryQueryParams = {}) {
    return this.http.get(`v1/business/${businessId}/inventory/movements`, { params })
  }

  createManualMovement(businessId: number, payload: InventoryPayload) {
    return this.http.post(`v1/business/${businessId}/inventory/movements/manual`, payload)
  }

  createTransfer(businessId: number, payload: InventoryPayload) {
    return this.http.post(`v1/business/${businessId}/inventory/movements/transfer`, payload)
  }

  createRecipe(businessId: number, payload: InventoryPayload) {
    return this.http.post(`v1/business/${businessId}/inventory/recipes`, payload)
  }

  getRecipeByItem(businessId: number, itemId: number) {
    return this.http.get(`v1/business/${businessId}/inventory/recipes/item/${itemId}`)
  }

  getRecipeComponents(businessId: number, recipeId: number) {
    return this.http.get(`v1/business/${businessId}/inventory/recipes/${recipeId}/components`)
  }

  uploadItems(businessId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)

    return this.http.post(`v1/business/${businessId}/inventory/items/upload`, formData)
  }

  uploadInitialStock(businessId: number, file: File, options: UploadInitialStockOptions = {}) {
    const formData = new FormData()
    formData.append('file', file)
    if (options.warehouseId !== undefined && options.warehouseId !== null && options.warehouseId !== '') {
      formData.append('warehouseId', String(options.warehouseId))
    }
    if (options.warehouseCode) {
      formData.append('warehouseCode', options.warehouseCode)
    }

    return this.http.post(`v1/business/${businessId}/inventory/stock/upload-initial`, formData)
  }
}

export const inventoryApi = new InventoryAPI()
export default inventoryApi
